import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { getHomeContent } from '@/lib/api';
import { trackVisit } from '@/lib/api';
import { 
  Settings, 
  Phone, 
  Mail, 
  ChevronRight,
  Wrench,
  Gauge,
  Zap,
  Award
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import ChatBot from '@/components/ChatBot';
import { FacebookReviews, FacebookPosts, InstagramFeed } from '@/components/MetaSections';
import { TikTokSection } from '@/components/TikTokSection';

const DEFAULT_CONTENT = {
  hero_title: "NAKI TUNE 2.0",
  hero_subtitle: "Professional Dyno Tuning Services",
  hero_description: "Unlock your vehicle's true potential with precision dyno tuning. We specialize in performance optimization for all makes and models.",
  about_title: "What We Do",
  about_description: "NAKI TUNE 2.0 offers professional dynamometer tuning services using our Mainline Pro Hub 2WD Dyno. We are authorised dealers for Link ECU and Maxx ECU, providing expert tuning and installation services.",
  services: [
    { title: "Carburetor Tuning and Service", description: "Professional carburetor tuning and servicing", icon: "gauge" },
    { title: "Link ECU Dealer", description: "Authorised Link ECU sales, installation & tuning", icon: "zap" },
    { title: "Maxx ECU", description: "Maxx ECU specialist tuning", icon: "award" },
    { title: "ECU Tuning", description: "Custom maps for maximum performance", icon: "wrench" }
  ],
  gallery_images: [],
  contact_phone: "0278150446",
  contact_email: "Nakitune@outlook.co.nz",
  contact_name: "William"
};

const ServiceCard = ({ service, getIcon }) => (
  <div className="glass p-6 border border-zinc-800 bg-zinc-900/50 text-center group hover:border-red-600/50 transition-colors">
    <div className="w-16 h-16 mx-auto mb-4 bg-green-600/20 border border-green-600/30 flex items-center justify-center text-green-500 group-hover:bg-green-600 group-hover:text-white transition-colors">
      {getIcon(service.icon)}
    </div>
    <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase text-white mb-2">
      {service.title}
    </h3>
    <p className="text-zinc-500 text-sm">
      {service.description}
    </p>
  </div>
);

const GalleryImage = ({ image }) => (
  <div className="aspect-square overflow-hidden border border-zinc-800">
    <img 
      src={image.url} 
      alt={image.caption || 'Gallery image'} 
      className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
    />
  </div>
);


export default function Home() {
  const navigate = useNavigate();
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trackVisit().catch(() => {});
    const fetchContent = async () => {
      try {
        const res = await getHomeContent();
        if (res.data) setContent(res.data);
      } catch (error) {
        console.log('Using default content');
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const getIcon = (iconName) => {
    switch(iconName) {
      case 'gauge': return <Gauge className="w-8 h-8" />;
      case 'zap': return <Zap className="w-8 h-8" />;
      case 'award': return <Award className="w-8 h-8" />;
      default: return <Wrench className="w-8 h-8" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const services = content.services || [];
  const galleryImages = content.gallery_images || [];

  return (
    <div className="min-h-screen bg-zinc-950 relative overflow-hidden">
      {/* Ghost Text Background */}
      <div className="ghost-text">NAKI TUNE 2.0</div>
      
      {/* Background Image - positioned at top */}
      <div 
        className="absolute inset-0 z-0 opacity-15"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1752774579270-523a9e91e6d4?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />

      {/* Navigation */}
      <Navigation />

      {/* Hero Section */}
      <section className="relative z-10 pt-20 pb-16 md:pt-32 md:pb-24">
        <div className="container mx-auto px-4 text-center">
          <div className="animate-fade-in-up">
            <h1 className="brand-title-green text-5xl md:text-8xl uppercase mb-4">
              {content.hero_title}
            </h1>
            <p className="text-xl md:text-2xl text-green-500 font-['Barlow_Condensed'] uppercase tracking-wide mb-6">
              {content.hero_subtitle}
            </p>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto mb-8">
              {content.hero_description}
            </p>
            <Button 
              onClick={() => navigate('/book')}
              className="bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wider px-8 py-6 text-lg btn-glow"
              data-testid="book-now-btn"
            >
              Book Your Session
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="relative z-10 py-16 md:py-24 border-t border-zinc-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-['Barlow_Condensed'] text-3xl md:text-5xl font-bold uppercase tracking-tight text-white text-center mb-8">
              {content.about_title}
            </h2>
            <p className="text-zinc-400 text-lg text-center mb-12">
              {content.about_description}
            </p>

            {/* Services Grid */}
            {services.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {services.map((service, index) => (
                  <ServiceCard key={index} service={service} getIcon={getIcon} />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      {galleryImages.length > 0 && (
        <section className="relative z-10 py-16 border-t border-zinc-800">
          <div className="container mx-auto px-4">
            <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold uppercase tracking-tight text-white text-center mb-8">
              Gallery
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {galleryImages.map((img, index) => (
                <GalleryImage key={index} image={img} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Facebook Reviews - Live from Meta API */}
      <FacebookReviews />

      {/* Facebook Posts - Live from Meta API */}
      <FacebookPosts />

      {/* Instagram Feed - Live from Meta API */}
      <InstagramFeed />

      {/* TikTok Feed */}
      <TikTokSection />

      {/* Contact Section */}
      <section className="relative z-10 py-16 border-t border-zinc-800">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold uppercase tracking-tight text-white mb-8">
            Get In Touch
          </h2>
          <div className="inline-block glass p-8 border border-zinc-800 bg-zinc-900/50">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <a 
                href={`tel:${content.contact_phone}`}
                className="flex items-center gap-3 text-white hover:text-green-500 transition-colors"
              >
                <Phone className="w-5 h-5" />
                <span className="font-['JetBrains_Mono']">{content.contact_name}: {content.contact_phone}</span>
              </a>
              <a 
                href={`mailto:${content.contact_email}`}
                className="flex items-center gap-3 text-white hover:text-green-500 transition-colors"
              >
                <Mail className="w-5 h-5" />
                <span className="font-['JetBrains_Mono']">{content.contact_email}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 py-8 border-t border-zinc-800 bg-zinc-950">
        <div className="container mx-auto px-4 text-center">
          <p className="text-zinc-600 text-sm">
            © 2026 NAKI TUNE 2.0. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Chatbot */}
      <ChatBot />
    </div>
  );
}
